import { useState, useMemo } from "react";
import foodData from "./FoodNutrientsTable";
import SharedHelmet from "../Helmet";
import { SecondAds } from "../../Ads";

const TITLE = "حاسبة القيمة الغذائية للطعام - حساب السعرات والبروتين والدهون والكربوهيدرات والسكريات";
const DESCRIPTION = "احسب القيم الغذائية للطعام بما في ذلك السعرات الحرارية، البروتين، الدهون، الكربوهيدرات، والسكريات بناءً على وزن الحصة المدخلة. أضف أطعمة جديدة لتحسين دقة حساباتك.";
const KEYWORDS = "حاسبة غذائية, قيمة غذائية, طعام, سعرات حرارية, بروتين, دهون, كربوهيدرات, سكر, حمية, تخطيط وجبات, إضافة أطعمة";
const OG_URL =
  "https://do-calculate.com/calculator/ar/food-nutrients-calculator/";


const TYPE_ICONS = {
  "خضروات": "🥦", "فواكه": "🍎", "لحوم": "🥩", "بيض": "🥚",
  "ألبان": "🥛", "خبز وحبوب": "🌾", "أكلات شعبية": "🍲",
  "وجبات خفيفة": "🍿", "مكسرات": "🥜", "مشروبات": "🥤",
  "دهون": "🫙", "كريمات": "🍦",
};

const DAILY = { protein: 50, fat: 65, carbohydrates: 300, sugar: 50 };

const C = {
  green: "#198754",
  greenLight: "#d1e7dd",
  greenDim: "#19875415",
  bgAlt: "#f6fdf9",
  border: "#dee2e6",
  text: "#1a2e24",
  textMid: "#4a6b58",
  textLight: "#8aab97",
  white: "#ffffff",
  danger: "#dc3545",
  protein: "#0d6efd",
  fat: "#fd7e14",
  carbs: "#198754",
  sugar: "#d63384",
};

const toEn = (s) => {
  const ar = ["٠", "١", "٢", "٣", "٤", "٥", "٦", "٧", "٨", "٩"];
  return String(s).replace(/[٠-٩]/g, d => ar.indexOf(d).toString());
};

function MacroBar({ label, value, target, color }) {
  const pct = Math.min((value / target) * 100, 100);
  const over = value > target;
  return (
    <div style={{ marginBottom: 7 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, marginBottom: 3 }}>
        <span style={{ color: C.textMid }}>{label}</span>
        <span style={{ fontWeight: 600, color: over ? C.danger : C.textMid }}>
          {value.toFixed(1)}غ
          <span style={{ fontWeight: 400, color: C.textLight }}> / {target}غ</span>
        </span>
      </div>
      <div style={{ height: 5, background: "#e9ecef", borderRadius: 999 }}>
        <div style={{ height: "100%", width: `${pct}%`, borderRadius: 999, background: over ? C.danger : color, transition: "width 0.4s ease" }} />
      </div>
    </div>
  );
}

function SearchPanel({ search, setSearch, selType, setSelType, types, filtered, flash, addFood, mealSummary }) {
  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", background: C.white, overflow: "hidden", borderLeft: `1px solid ${C.border}`, minHeight: 0 }}>

      {/* Search + chips */}
      <div style={{ flexShrink: 0, padding: "12px 14px 8px", borderBottom: `1px solid ${C.border}` }}>
        <div style={{ position: "relative", marginBottom: 10 }}>
          <span style={{ position: "absolute", right: 11, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}>🔍</span>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="ابحث عن أي طعام..."
            style={{
              width: "100%", padding: "9px 34px 9px 12px",
              border: `1.5px solid ${C.border}`, borderRadius: 8,
              fontSize: 16, fontFamily: "inherit", color: C.text,
              background: C.bgAlt, outline: "none", boxSizing: "border-box",
            }}
            onFocus={e => e.target.style.borderColor = C.green}
            onBlur={e => e.target.style.borderColor = C.border}
          />
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
          {types.map(t => (
            <button key={t} onClick={() => setSelType(t)} style={{
              padding: "4px 10px", borderRadius: 999, fontSize: 14,
              border: "none", fontFamily: "inherit", cursor: "pointer",
              whiteSpace: "nowrap", fontWeight: t === selType ? 700 : 500,
              background: t === selType ? C.green : C.bgAlt,
              color: t === selType ? C.white : C.textMid,
            }}>
              {TYPE_ICONS[t] || ""} {t}
            </button>
          ))}
        </div>
      </div>

      {/* Mobile meal summary bar — rendered above the count line */}
      {mealSummary}

      <div style={{ flexShrink: 0, padding: "4px 14px", fontSize: 14, color: C.textLight, background: "#f0faf5", borderBottom: `1px solid ${C.border}` }}>
        {filtered.length} طعام متاح · اضغط لإضافته للوجبة
      </div>

      {/* Scrollable food list */}
      <div style={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden", touchAction: "pan-y", padding: "6px 10px" }}>
        {filtered.length === 0 && (
          <div style={{ textAlign: "center", color: C.textLight, padding: 40, fontSize: 14 }}>لا توجد نتائج 🤷</div>
        )}

        {/* Desktop: 2-col grid */}
        <div className="food-grid-desktop">
          {filtered.map(food => {
            const lit = flash === food.food;
            return (
              <div key={food.food} onClick={() => addFood(food)} style={{
                display: "flex", flexDirection: "column", justifyContent: "space-between",
                padding: "10px 10px 8px", borderRadius: 10, cursor: "pointer",
                background: lit ? C.greenLight : C.white,
                border: `1.5px solid ${lit ? C.green : C.border}`,
                transition: "all .15s",
              }}
                onMouseEnter={e => { if (!lit) e.currentTarget.style.background = C.bgAlt; }}
                onMouseLeave={e => { if (!lit) e.currentTarget.style.background = C.white; }}
              >
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 5 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: C.text, lineHeight: 1.3 }}>{food.food}</div>
                    <div style={{ fontSize: 14, color: C.textLight, marginTop: 1 }}>{TYPE_ICONS[food.type] || ""} قطعة ({food.weight} غرام)</div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 5, flexShrink: 0, marginRight: 6 }}>
                    <span style={{ fontSize: 14, fontWeight: 700, padding: "2px 6px", borderRadius: 999, background: C.greenDim, color: C.green, whiteSpace: "nowrap" }}>
                      {food.calories.toFixed(0)} 🔥
                    </span>
                    <div style={{ width: 22, height: 22, borderRadius: "50%", background: C.green, color: C.white, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0 }}>+</div>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 12, fontWeight: 600, padding: "2px 7px", borderRadius: 999, background: "#dbeafe", color: "#1d4ed8" }}>بروتين {(food.protein || 0).toFixed(1)}غ</span>
                  <span style={{ fontSize: 12, fontWeight: 600, padding: "2px 7px", borderRadius: 999, background: "#fef3c7", color: "#b45309" }}>دهون {(food.fat || 0).toFixed(1)}غ</span>
                  <span style={{ fontSize: 12, fontWeight: 600, padding: "2px 7px", borderRadius: 999, background: "#dcfce7", color: "#15803d" }}>كربو {(food.carbohydrates || 0).toFixed(1)}غ</span>
                  {(food.sugar != null && food.sugar > 0) && (
                    <span style={{ fontSize: 12, fontWeight: 600, padding: "2px 7px", borderRadius: 999, background: "#fce7f3", color: "#9d174d" }}>سكر {food.sugar.toFixed(1)}غ</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Mobile: compact rows */}
        <div className="food-list-mobile">
          {filtered.map(food => {
            const lit = flash === food.food;
            return (
              <div key={food.food} onClick={() => addFood(food)} style={{
                display: "flex", flexDirection: "row", alignItems: "center",
                padding: "7px 8px", borderRadius: 8, cursor: "pointer", gap: 8, marginBottom: 4,
                background: lit ? C.greenLight : C.white,
                border: `1.5px solid ${lit ? C.green : C.border}`,
                transition: "all .15s",
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 15, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {food.food}
                  </div>
                  <div style={{ display: "flex", gap: 4, marginTop: 3, alignItems: "center", flexWrap: "wrap" }}>
                    <span style={{ fontSize: 13, color: C.textLight, whiteSpace: "nowrap" }}>{TYPE_ICONS[food.type] || ""} {food.weight}غ</span>
                    <span style={{ fontSize: 13, fontWeight: 700, padding: "1px 5px", borderRadius: 999, background: "#dbeafe", color: "#1d4ed8", whiteSpace: "nowrap" }}>بروتين {(food.protein || 0).toFixed(1)}غ</span>
                    <span style={{ fontSize: 13, fontWeight: 700, padding: "1px 5px", borderRadius: 999, background: "#fef3c7", color: "#b45309", whiteSpace: "nowrap" }}>دهون {(food.fat || 0).toFixed(1)}غ</span>
                    <span style={{ fontSize: 13, fontWeight: 700, padding: "1px 5px", borderRadius: 999, background: "#dcfce7", color: "#15803d", whiteSpace: "nowrap" }}>كربو {(food.carbohydrates || 0).toFixed(1)}غ</span>
                    {(food.sugar != null && food.sugar > 0) && (
                      <span style={{ fontSize: 13, fontWeight: 700, padding: "1px 5px", borderRadius: 999, background: "#fce7f3", color: "#9d174d", whiteSpace: "nowrap" }}>سكر {food.sugar.toFixed(1)}غ</span>
                    )}
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, padding: "2px 7px", borderRadius: 999, background: C.greenDim, color: C.green, whiteSpace: "nowrap" }}>
                    {food.calories.toFixed(0)} 🔥
                  </span>
                  <div style={{ width: 30, height: 30, borderRadius: "50%", background: C.green, color: C.white, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>+</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function MealPanel({ meal, totals, editingId, setEditingId, editQty, setEditQty, saveEdit, removeItem, setMeal, onBack }) {
  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", background: C.bgAlt, overflow: "hidden", minHeight: 0 }}>

      <div style={{ padding: "12px 14px", background: C.white, borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexShrink: 0 }}>
        {onBack && (
          <button onClick={onBack} style={{
            background: C.greenDim, border: `1px solid ${C.greenLight}`,
            borderRadius: 8, padding: "5px 10px", cursor: "pointer",
            fontFamily: "inherit", fontSize: 12, fontWeight: 700, color: C.green, flexShrink: 0,
          }}>
            ＋ إضافة
          </button>
        )}
        <span style={{ fontWeight: 700, fontSize: 14, color: C.text, flex: 1 }}>
          🍽 وجبتي
          {meal.length > 0 && (
            <span style={{ marginRight: 7, background: C.green, color: C.white, borderRadius: 999, padding: "1px 8px", fontSize: 11 }}>{meal.length}</span>
          )}
        </span>
        {meal.length > 0 && (
          <button onClick={() => setMeal([])} style={{ fontSize: 11.5, color: C.danger, background: "none", border: "none", fontFamily: "inherit", cursor: "pointer", flexShrink: 0 }}>
            مسح الكل ✕
          </button>
        )}
      </div>

      {meal.length > 0 && (
        <div style={{ flexShrink: 0, padding: "6px 14px", fontSize: 11, fontWeight: 700, color: C.textLight, background: C.white, borderBottom: `1px solid ${C.border}` }}>
          تفاصيل الوجبة
        </div>
      )}

      {meal.length === 0 ? (
        /* Empty state — no flex:1 so totals sit right below */
        <div style={{ flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: C.textLight, textAlign: "center", padding: "32px 24px 24px" }}>
          <div style={{ fontSize: 48, marginBottom: 10 }}>🍽</div>
          <div style={{ fontWeight: 700, fontSize: 15, color: C.textMid }}>الوجبة فارغة</div>
          <div style={{ fontSize: 12, marginTop: 5 }}>اضغط على أي طعام لإضافته هنا</div>
        </div>
      ) : (
        <div style={{ flexShrink: 0, maxHeight: 320, overflowY: "auto", overflowX: "hidden", touchAction: "pan-y", padding: "8px 12px" }}>
          {meal.map(item => {
            const kcal = (item.food.calories * item.qty / item.food.size).toFixed(0);
            return (
              <div key={item.id} style={{ display: "flex", alignItems: "center", gap: 7, padding: "9px 11px", borderRadius: 10, marginBottom: 6, background: C.white, border: `1.5px solid ${C.border}` }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {TYPE_ICONS[item.food.type] || ""} {item.food.food}
                  </div>
                  <div style={{ fontSize: 10.5, color: C.textLight, marginTop: 1 }}>{item.food.serving}</div>
                </div>
                {editingId === item.id ? (
                  <input autoFocus value={editQty}
                    onChange={e => setEditQty(e.target.value)}
                    onBlur={() => saveEdit(item.id)}
                    onKeyDown={e => e.key === "Enter" && saveEdit(item.id)}
                    style={{ width: 52, padding: "4px 6px", borderRadius: 6, border: `1.5px solid ${C.green}`, fontFamily: "inherit", fontSize: 12, textAlign: "center", outline: "none", color: C.text }}
                  />
                ) : (
                  <div onClick={() => { setEditingId(item.id); setEditQty(String(item.qty)); }}
                    style={{ padding: "4px 8px", borderRadius: 6, fontSize: 12, border: `1px solid ${C.border}`, background: C.bgAlt, cursor: "pointer", color: C.textMid, minWidth: 44, textAlign: "center" }}>
                    ×{item.qty}
                  </div>
                )}
                <div style={{ fontSize: 12, fontWeight: 700, color: C.green, background: C.greenDim, borderRadius: 6, padding: "3px 7px", minWidth: 58, textAlign: "center" }}>
                  {kcal} سعرة
                </div>
                <button onClick={() => removeItem(item.id)} style={{ background: "none", border: "none", color: "#ccc", cursor: "pointer", fontSize: 16, padding: "0 2px", flexShrink: 0 }}>✕</button>
              </div>
            );
          })}
        </div>
      )}

      <div style={{ flexShrink: 0, padding: "10px 12px", borderTop: `1px solid ${C.border}`, background: C.bgAlt }}>
        <div style={{ background: `linear-gradient(135deg, ${C.green} 0%, #1a9e62 100%)`, borderRadius: 12, padding: "12px 16px", marginBottom: 8, display: "flex", justifyContent: "space-between", alignItems: "center", color: C.white }}>
          <div>
            <div style={{ fontSize: 14, opacity: .85 }}>إجمالي السعرات</div>
            <div style={{ fontSize: 28, fontWeight: 800, lineHeight: 1.1 }}>
              {totals.calories.toFixed(0)}
              <span style={{ fontSize: 12, fontWeight: 400, opacity: .8, marginRight: 4 }}>سعرة</span>
            </div>
          </div>
          <div style={{ textAlign: "left" }}>
            <div style={{ fontSize: 14, opacity: .85 }}>الوزن الكلي</div>
            <div style={{ fontSize: 16, fontWeight: 700 }}>{totals.weight.toFixed(0)} غ</div>
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 6, marginBottom: meal.length > 0 ? 8 : 0 }}>
          {[
            { l: "بروتين", v: totals.protein, c: C.protein },
            { l: "دهون", v: totals.fat, c: C.fat },
            { l: "كربوهيد", v: totals.carbohydrates, c: C.carbs },
            { l: "سكر", v: totals.sugar, c: C.sugar },
          ].map(({ l, v, c }) => (
            <div key={l} style={{ background: C.white, borderRadius: 8, padding: "7px 4px", textAlign: "center", border: `1px solid ${C.border}` }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: c }}>{v.toFixed(1)}غ</div>
              <div style={{ fontSize: 14, color: C.textLight, marginTop: 1 }}>{l}</div>
            </div>
          ))}
        </div>
        {meal.length > 0 && (
          <div style={{ background: C.white, borderRadius: 10, padding: "10px 12px", border: `1px solid ${C.border}` }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: C.textMid, marginBottom: 8 }}>نسبة الاحتياج اليومي</div>
            <MacroBar label="بروتين" value={totals.protein} target={DAILY.protein} color={C.protein} />
            <MacroBar label="دهون" value={totals.fat} target={DAILY.fat} color={C.fat} />
            <MacroBar label="كربوهيدرات" value={totals.carbohydrates} target={DAILY.carbohydrates} color={C.carbs} />
            <MacroBar label="سكر" value={totals.sugar} target={DAILY.sugar} color={C.sugar} />
          </div>
        )}
      </div>
    </div>
  );
}

export default function FoodCalculator() {
  const [search, setSearch] = useState("");
  const [selType, setSelType] = useState("الكل");
  const [meal, setMeal] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editQty, setEditQty] = useState("");
  const [flash, setFlash] = useState(null);
  const [mobileTab, setMobileTab] = useState("search");

  const types = useMemo(() => ["الكل", ...new Set(foodData.map(f => f.type))], []);
  const filtered = useMemo(() => foodData.filter(f => {
    const okType = selType === "الكل" || f.type === selType;
    const okSearch = !search || f.food.includes(search) || f.type.includes(search);
    return okType && okSearch;
  }), [search, selType]);

  const totals = useMemo(() => meal.reduce((acc, item) => {
    const m = item.qty / item.food.size;
    acc.calories += item.food.calories * m;
    acc.protein += item.food.protein * m;
    acc.fat += (item.food.fat || 0) * m;
    acc.carbohydrates += item.food.carbohydrates * m;
    acc.sugar += (item.food.sugar || 0) * m;
    acc.weight += item.food.weight * m;
    return acc;
  }, { calories: 0, protein: 0, fat: 0, carbohydrates: 0, sugar: 0, weight: 0 }), [meal]);

  const addFood = (food) => {
    setMeal(prev => {
      const ex = prev.find(m => m.food.food === food.food);
      if (ex) return prev.map(m => m.food.food === food.food ? { ...m, qty: +(m.qty + food.size).toFixed(4) } : m);
      return [...prev, { id: Date.now(), food, qty: food.size }];
    });
    setFlash(food.food);
    setTimeout(() => setFlash(null), 800);
  };

  const removeItem = (id) => setMeal(prev => prev.filter(m => m.id !== id));
  const saveEdit = (id) => {
    const v = parseFloat(toEn(editQty));
    if (!isNaN(v) && v > 0) setMeal(prev => prev.map(m => m.id === id ? { ...m, qty: v } : m));
    setEditingId(null);
  };

  // Mobile meal summary bar — shown above the count line inside SearchPanel
  const mobileMealSummary = meal.length > 0 ? (
    <div
      onClick={() => setMobileTab("meal")}
      className="mobile-meal-summary"
      style={{
        flexShrink: 0, background: C.green, color: C.white,
        padding: "10px 16px", cursor: "pointer",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        borderBottom: `1px solid ${C.greenLight}`,
      }}
    >
      <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
        <div>
          <div style={{ fontSize: 14, opacity: .8 }}>إجمالي السعرات</div>
          <div style={{ fontSize: 20, fontWeight: 800, lineHeight: 1 }}>{totals.calories.toFixed(0)} <span style={{ fontSize: 14, fontWeight: 400 }}>سعرة</span></div>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {[
            { l: "بروتين", v: totals.protein },
            { l: "دهون", v: totals.fat },
            { l: "كربو", v: totals.carbohydrates },
          ].map(({ l, v }) => (
            <div key={l} style={{ background: "rgba(255,255,255,.2)", borderRadius: 6, padding: "3px 7px", textAlign: "center" }}>
              <div style={{ fontSize: 14, fontWeight: 700 }}>{v.toFixed(1)}غ</div>
              <div style={{ fontSize: 14, opacity: .85 }}>{l}</div>
            </div>
          ))}
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 15, opacity: .9 }}>
        <strong className="text-white">
          <span>الوجبة ({meal.length})</span>
          <span style={{ fontSize: 15 }}>←</span>
        </strong>
      </div>
    </div>
  ) : null;

  const sp = { search, setSearch, selType, setSelType, types, filtered, flash, addFood };
  const mp = { meal, totals, editingId, setEditingId, editQty, setEditQty, saveEdit, removeItem, setMeal };

  return (
    <div style={{ fontFamily: "'Noto Kufi Arabic','Cairo',sans-serif", direction: "rtl", background: C.bgAlt, color: C.text, display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
      />
      <style>{`
        *, *::before, *::after { box-sizing: border-box; }
        html, body { overflow-x: hidden; margin: 0; }
        ::-webkit-scrollbar { width:4px; }
        ::-webkit-scrollbar-track { background:#edf7f2; }
        ::-webkit-scrollbar-thumb { background:#aed6be; border-radius:999px; }

        .food-grid-desktop { display: grid; grid-template-columns: 1fr 1fr; gap: 6px; }
        .food-list-mobile  { display: none; }
        .fc-desktop        { display: flex !important; }
        .fc-mob            { display: none !important; }
        .fc-mob-tabs       { display: none !important; }
        .mobile-meal-summary { display: none !important; }

        @media (max-width: 640px) {
          .food-grid-desktop   { display: none; }
          .food-list-mobile    { display: block; }
          .fc-desktop          { display: none !important; }
          .fc-mob              { display: flex !important; }
          .fc-mob-tabs         { display: flex !important; }
          .mobile-meal-summary { display: flex !important; }
        }
      `}</style>
      <br />
      <div
        className="bg-success text-white text-center"
      >
        <h1 style={{ fontSize: "17px" }}>{TITLE}</h1>
      </div>
      <div className="border border-light">
        <div>
          <p>
            هل تبحث عن أداة سهلة الاستخدام تساعدك على اتخاذ قرارات غذائية أفضل؟ هذه الحاسبة الغذائية الشاملة هي الحل الأمثل لك! بفضل واجهتها البسيطة وقاعدة البيانات الواسعة، يمكنك بسهولة حساب السعرات الحرارية، البروتين، الدهون، الكربوهيدرات، والسكريات في أي طعام. سواء كنت ترغب في خسارة الوزن، بناء العضلات، أو ببساطة الحفاظ على صحة جيدة، فإن هذه الأداة هي صديقك الأمين في رحلتك الغذائية. استخدمها لتتبع تقدمك نحو تحقيق أهدافك الغذائية، وقارن بين مختلف الأطعمة، وأضف أطعمة جديدة لتحسين دقة حساباتك. لا تدع الغموض يحيط بغذائك، استخدم هذه الحاسبة واستمتع بتجربة غذائية أكثر وعيًا ومرونة.
          </p>
          <b>
            <u>كيف تعمل؟</u>
          </b>
          <ul>
            <li>
              اختر نوع الطعام: من القائمة المنسدلة، اختر نوع الطعام الذي ترغب في تحليله.
            </li>
            <li>
              ابحث عن الطعام: أدخل اسم الطعام في مربع البحث وستظهر لك قائمة من الخيارات المطابقة.
            </li>
            <li>
              حدد حجم الحصة: اختر حجم الحصة المناسب لك من القائمة المنسدلة أو أدخل قيمة مخصصة.
            </li>
            <li>
              احصل على النتائج: ستقوم الحاسبة على الفور بحساب السعرات الحرارية، البروتين، الدهون، الكربوهيدرات، والسكريات للطعام المحدد.
            </li>
            <li>
              أضف أطعمة جديدة: يمكنك إضافة أطعمة جديدة لتحسين دقة حساباتك وحساب القيمة الغذائية لمجموعة متنوعة من الأطعمة.
            </li>
          </ul>
          تم تطوير الحاسبة بناءاً على موقع وزارة الصحة البحريني:{" "}
          <a
            href="https://www.moh.gov.bh/healthinfo/foods"
            rel="nofollow"
            alt="وزارة الصحة البحرينية"
          >
            وزارة الصحة البحرينية
          </a>
          <hr />
        </div>
      </div>
      <SecondAds />
      <br />
      {/* Header */}
      <div style={{ background: C.green, color: C.white, padding: "13px 18px", display: "flex", alignItems: "center", justifyContent: "space-between", boxShadow: "0 2px 6px #19875430" }}>
        <div>
          <h2 style={{ fontWeight: 800, fontSize: 17 }}>🥗 حاسبة القيمة الغذائية للطعام</h2>
          <div style={{ fontSize: 11, opacity: .85, marginTop: 2 }}>احسب سعراتك وعناصرك الغذائية بسهولة</div>
        </div>
        {meal.length > 0 && (
          <div style={{ background: "rgba(255,255,255,.2)", borderRadius: 10, padding: "6px 14px", textAlign: "center" }}>
            <div style={{ fontSize: 22, fontWeight: 800, lineHeight: 1 }}>{totals.calories.toFixed(0)}</div>
            <div style={{ fontSize: 10, opacity: .85 }}>سعرة حرارية</div>
          </div>
        )}

      </div>

      {/* Mobile tab bar */}
      <div className="fc-mob-tabs" style={{ background: C.white, borderBottom: `2px solid ${C.border}` }}>
        {[
          { key: "search", label: "🔍 الأطعمة" },
          { key: "meal", label: `🍽 وجبتي${meal.length > 0 ? ` (${meal.length})` : ""}` },
        ].map(tab => (
          <button key={tab.key} onClick={() => setMobileTab(tab.key)} style={{
            flex: 1, padding: "11px 6px", border: "none", background: "none",
            fontFamily: "inherit", fontSize: 13, fontWeight: 600, cursor: "pointer",
            color: mobileTab === tab.key ? C.green : C.textLight,
            borderBottom: `2.5px solid ${mobileTab === tab.key ? C.green : "transparent"}`,
          }}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Desktop */}
      <div className="fc-desktop" style={{ flex: 1, overflow: "hidden", minHeight: 0 }}>
        <SearchPanel {...sp} />
        <MealPanel   {...mp} />
      </div>

      {/* Mobile */}
      <div className="fc-mob" style={{ flex: 1, flexDirection: "column", overflow: "hidden", minHeight: 0 }}>
        {mobileTab === "search" ? (
          <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", minHeight: 0 }}>
            <SearchPanel {...sp} mealSummary={mobileMealSummary} />
          </div>
        ) : (
          <MealPanel {...mp} onBack={() => setMobileTab("search")} />
        )}
      </div>


    </div>
  );
}